import React, { useState, useRef, useEffect } from 'react';
import {
  Camera,
  Upload,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Trash2,
  User,
  Sparkles,
  X,
  Check,
} from 'lucide-react';

export interface ProfilePhotoUploaderProps {
  photoUrl?: string | null;
  onChange: (photoUrl: string) => void;
  label?: string;
  helperText?: string;
  userRole?: 'customer' | 'professional';
  showPresets?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// Curated Nigerian and African representation portraits for instant 1-click selection
const CUSTOMER_PRESET_AVATARS = [
  {
    id: 'cust-1',
    label: 'Professional Lady (Lagos)',
    url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&auto=format&fit=crop&q=80',
  },
  {
    id: 'cust-2',
    label: 'Gentleman in Blazer (Abuja)',
    url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&auto=format&fit=crop&q=80',
  },
  {
    id: 'cust-3',
    label: 'Smiling Lady (Port Harcourt)',
    url: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&auto=format&fit=crop&q=80',
  },
  {
    id: 'cust-4',
    label: 'Young Executive (Ibadan)',
    url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&auto=format&fit=crop&q=80',
  },
];

const PROFESSIONAL_PRESET_AVATARS = [
  {
    id: 'pro-1',
    label: 'Senior Artisan / Engineer',
    url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&auto=format&fit=crop&q=80',
  },
  {
    id: 'pro-2',
    label: 'Certified Female Technician',
    url: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&auto=format&fit=crop&q=80',
  },
  {
    id: 'pro-3',
    label: 'Field Master Craftsman',
    url: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&auto=format&fit=crop&q=80',
  },
  {
    id: 'pro-4',
    label: 'Licensed Trade Specialist',
    url: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&auto=format&fit=crop&q=80',
  },
];

/**
 * Resizes and compresses an image file to a lightweight data URL (< 250KB)
 * to ensure fast uploads, immediate previews, and zero lag.
 */
async function compressImageToDataUrl(file: File, maxDimension = 400): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to load image for processing.'));
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Crop to square aspect ratio centered
          const minSide = Math.min(width, height);
          const startX = (width - minSide) / 2;
          const startY = (height - minSide) / 2;

          const targetSize = Math.min(minSide, maxDimension);
          canvas.width = targetSize;
          canvas.height = targetSize;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(String(reader.result));
            return;
          }

          // Smooth rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          ctx.drawImage(
            img,
            startX,
            startY,
            minSide,
            minSide,
            0,
            0,
            targetSize,
            targetSize
          );

          // Export as compressed JPEG
          const dataUrl = canvas.toDataURL('image/jpeg', 0.86);
          resolve(dataUrl);
        } catch {
          resolve(String(reader.result));
        }
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

export const ProfilePhotoUploader: React.FC<ProfilePhotoUploaderProps> = ({
  photoUrl,
  onChange,
  label = 'Profile Photograph',
  helperText = 'Upload a clear, friendly photo for your profile and quotes.',
  userRole = 'customer',
  showPresets = true,
  size = 'md',
  className = '',
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successBadge, setSuccessBadge] = useState<string | null>(null);
  const [showPresetsPanel, setShowPresetsPanel] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const presets = userRole === 'professional' ? PROFESSIONAL_PRESET_AVATARS : CUSTOMER_PRESET_AVATARS;
  const currentPhoto = photoUrl || presets[0].url;

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    setSuccessBadge(null);

    // Validate size (< 8MB before compression)
    if (file.size > 8 * 1024 * 1024) {
      setErrorMessage('Image size exceeds 8MB. Please select a smaller photo.');
      e.target.value = '';
      return;
    }

    // Validate format
    const lowerType = (file.type || '').toLowerCase();
    if (!lowerType.includes('jpeg') && !lowerType.includes('jpg') && !lowerType.includes('png') && !lowerType.includes('webp')) {
      setErrorMessage('Unsupported format. Please upload a JPG, PNG, or WebP photo.');
      e.target.value = '';
      return;
    }

    setIsProcessing(true);
    try {
      const dataUrl = await compressImageToDataUrl(file, 480);
      onChange(dataUrl);
      setSuccessBadge('Photo updated successfully');
      setTimeout(() => setSuccessBadge(null), 3000);
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to process image. Please try again.');
    } finally {
      setIsProcessing(false);
      e.target.value = '';
    }
  };

  const handlePresetSelect = (presetUrl: string) => {
    setErrorMessage(null);
    onChange(presetUrl);
    setShowPresetsPanel(false);
    setSuccessBadge('Avatar updated');
    setTimeout(() => setSuccessBadge(null), 2500);
  };

  const handleRemovePhoto = () => {
    setErrorMessage(null);
    onChange('');
    setSuccessBadge('Photo removed');
    setTimeout(() => setSuccessBadge(null), 2500);
  };

  // Live Camera Controls (for devices with front cameras)
  const startCamera = async () => {
    setErrorMessage(null);
    setIsCameraActive(true);

    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } },
          audio: false,
        });
        mediaStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } else {
        // Fallback to mobile file capture
        setIsCameraActive(false);
        cameraInputRef.current?.click();
      }
    } catch (err: any) {
      setIsCameraActive(false);
      // If camera access fails (permission denied or no device), open mobile capture file input
      cameraInputRef.current?.click();
    }
  };

  const stopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const captureCameraFrame = () => {
    if (!videoRef.current) return;
    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      const side = Math.min(video.videoWidth || 480, video.videoHeight || 480);
      canvas.width = 400;
      canvas.height = 400;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        const startX = ((video.videoWidth || 480) - side) / 2;
        const startY = ((video.videoHeight || 480) - side) / 2;
        ctx.drawImage(video, startX, startY, side, side, 0, 0, 400, 400);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
        onChange(dataUrl);
        setSuccessBadge('Camera photo captured');
        setTimeout(() => setSuccessBadge(null), 2500);
      }
    } catch (err) {
      setErrorMessage('Failed to capture snapshot from camera.');
    } finally {
      stopCamera();
    }
  };

  const dimensionClass =
    size === 'sm'
      ? 'w-16 h-16'
      : size === 'lg'
      ? 'w-24 h-24 sm:w-28 sm:h-28'
      : 'w-20 h-20 sm:w-24 sm:h-24';

  return (
    <div className={`space-y-3 ${className}`}>
      {label && (
        <div className="flex items-center justify-between">
          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
            {label}
          </label>
          {successBadge && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200 animate-in fade-in">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
              <span>{successBadge}</span>
            </span>
          )}
        </div>
      )}

      {/* Main Avatar + Action Controls */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-200/90">
        {/* Photo Display with Edit Overlay */}
        <div className="relative group shrink-0">
          <div className={`${dimensionClass} rounded-2xl overflow-hidden ring-2 ring-emerald-500/30 shadow-md bg-white flex items-center justify-center relative`}>
            {currentPhoto ? (
              <img
                src={currentPhoto}
                alt={label}
                className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-200"
              />
            ) : (
              <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400">
                <User className="w-8 h-8" />
              </div>
            )}

            {isProcessing && (
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-2xs flex items-center justify-center text-white">
                <RefreshCw className="w-5 h-5 animate-spin" />
              </div>
            )}
          </div>

          {/* Quick Click-to-Upload Badge */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            title="Upload photo from device"
            className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center shadow-md border-2 border-white cursor-pointer transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <Camera className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Buttons & Information */}
        <div className="flex-1 text-center sm:text-left space-y-2">
          {helperText && <p className="text-xs text-slate-500 leading-relaxed">{helperText}</p>}

          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-1">
            {/* 1. Device File Picker Button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
              className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer disabled:opacity-50"
            >
              <Upload className="w-3.5 h-3.5" />
              <span>Upload Photo</span>
            </button>

            {/* 2. Live Camera Capture Button */}
            <button
              type="button"
              onClick={startCamera}
              disabled={isProcessing}
              className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-100 font-bold text-xs flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
            >
              <Camera className="w-3.5 h-3.5 text-emerald-400" />
              <span>Take Photo</span>
            </button>

            {/* 3. Preset Avatars Toggle */}
            {showPresets && (
              <button
                type="button"
                onClick={() => setShowPresetsPanel(!showPresetsPanel)}
                className="px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 font-semibold text-xs flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                <span>{showPresetsPanel ? 'Hide Avatars' : 'Choose Avatar'}</span>
              </button>
            )}

            {/* 4. Remove / Reset Button */}
            {photoUrl && (
              <button
                type="button"
                onClick={handleRemovePhoto}
                className="px-2.5 py-1.5 rounded-xl text-rose-600 hover:bg-rose-50 border border-rose-200 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer"
                title="Remove photo"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Reset</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Error Message */}
      {errorMessage && (
        <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Preset Avatars Selection Tray */}
      {showPresets && showPresetsPanel && (
        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-2.5 animate-in fade-in duration-150">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              <span>Select from Curated {userRole === 'professional' ? 'Artisan' : 'Customer'} Avatars:</span>
            </span>
            <button
              type="button"
              onClick={() => setShowPresetsPanel(false)}
              className="text-slate-400 hover:text-slate-600 text-xs p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {presets.map((preset) => {
              const isSelected = photoUrl === preset.url;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handlePresetSelect(preset.url)}
                  className={`group relative rounded-xl overflow-hidden border-2 p-1 transition-all text-left cursor-pointer ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50/50 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300 bg-slate-50'
                  }`}
                >
                  <div className="aspect-square w-full rounded-lg overflow-hidden bg-slate-200 relative mb-1.5">
                    <img
                      src={preset.url}
                      alt={preset.label}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-xs">
                        <Check className="w-3 h-3 stroke-[3]" />
                      </div>
                    )}
                  </div>
                  <span className="block text-[11px] font-bold text-slate-800 truncate px-1">
                    {preset.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Live Webcam Modal */}
      {isCameraActive && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-in fade-in">
          <div className="bg-slate-900 text-white rounded-3xl p-6 max-w-sm w-full border border-slate-800 shadow-2xl space-y-4 text-center">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
              <span className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                <Camera className="w-4 h-4 text-emerald-400" />
                <span>Take Profile Photo</span>
              </span>
              <button
                type="button"
                onClick={stopCamera}
                className="text-slate-400 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="w-56 h-56 mx-auto rounded-2xl overflow-hidden ring-4 ring-emerald-500/40 bg-black relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>

            <p className="text-xs text-slate-400">Position your face in the center and click Capture.</p>

            <div className="flex items-center justify-center gap-3 pt-1">
              <button
                type="button"
                onClick={captureCameraFrame}
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md transition-colors cursor-pointer"
              >
                Capture Photo
              </button>
              <button
                type="button"
                onClick={stopCamera}
                className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
